import { useCallback } from "react";
import { usePostApi } from "src/hooks";
import { type ICredentials } from "src/interfaces";
import { useDispatch } from "src/redux/hooks";
import {
  setActiveAutosave,
  setCurrent,
  setOpenedSaveModal,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";
import { refetchLogbook, setLatestEntryStatus } from "src/redux/slices/generatorSlice";

import { generatorStore } from "../../store";
import { getCurrentUTC } from "../../../utils/timeUtils/getCurrentUTC";
import { useEntryNavigation } from "./useEntryNavigation";
import { useEntryParams } from "./useEntryParams";

export const useSaveEntry = (
  params: ReturnType<typeof useEntryParams>,
  navigation: ReturnType<typeof useEntryNavigation>,
) => {
  const { post, isLoading } = usePostApi();
  const dispatch = useDispatch();

  const {
    isV2,
    logbookId,
    getCommonParams,
    getRouteEntryId,
    current,
    entryIdType,
    lbName,
    lbeidOrPartId,
    isNewEntry,
  } = params;

  const { navigateToSavedEntry } = navigation;

  const saveEntry = useCallback(
    async (credentials: ICredentials) => {
      if (!isV2 || !logbookId) return;
      const baseParams = getCommonParams();
      if (!baseParams) return;

      const entryId = getRouteEntryId();
      const result = await post({
        request: "save-entry",
        body: {
          params: {
            _current: current,
            ...baseParams,
            ...credentials,
            [entryIdType]: entryId,
          },
          _lbid: logbookId,
          _lbname: lbName,
          ...(window?.api ? { _idInRoute: !!lbeidOrPartId } : {}),
        },
      });

      if (!result) return;

      generatorStore.getState().setDirty(false);

      dispatch(setOpenedSaveModal(false));
      dispatch(setActiveAutosave(false));
      dispatch(setReleaseEntryLock(Math.random()));
      dispatch(setCurrent(result.current || getCurrentUTC()));

      if (isNewEntry) {
        dispatch(setLatestEntryStatus({ status: 1, autosave: 0 }));
        navigateToSavedEntry(result);
      } else {
        dispatch(refetchLogbook());
      }
    },
    [
      current,
      dispatch,
      entryIdType,
      getCommonParams,
      getRouteEntryId,
      isNewEntry,
      isV2,
      lbName,
      lbeidOrPartId,
      logbookId,
      navigateToSavedEntry,
      post,
    ],
  );

  return { saveEntry, isSaving: isLoading };
};
